import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AuthInterceptor } from "../../services/AuthInterceptor.Service";
import Spinnerr from "../Commaon/Spinner";
import Toaster from "../Commaon/Toaster";
import SearchableMultiSelectModal from "../Commaon/SearchableMultiSelectModal";

const getRows = (data) => {
  if (Array.isArray(data?.r)) return data.r;
  if (Array.isArray(data?.r?.data)) return data.r.data;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const EditPackageModel = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // form states
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [ageLimit, setAgeLimit] = useState("");
  const [report, setReport] = useState("");
  const [fasting, setFasting] = useState("");
  const [originalPrice, setOriginalPrice] = useState("");
  const [discountedPrice, setDiscountedPrice] = useState("");
  const [discountPercent, setDiscountPercent] = useState("");
  const [gstSlab, setGstSlab] = useState("0");
  const [recommendedGender, setRecommendedGender] = useState("Both");
  const [sampleType, setSampleType] = useState("");
  const [testCriteria, setTestCriteria] = useState("");
  const [testInstruction, setTestInstruction] = useState("");
  const [testIds, setTestIds] = useState([]);
  const [availableTests, setAvailableTests] = useState([]);
  const [isActive, setIsActive] = useState(true);

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState(null);
  const normalizeId = useCallback((value) => {
    if (!value) return "";
    if (typeof value === "string") return value;
    if (typeof value === "object") {
      if (value.$oid) return value.$oid;
      if (value._id) return normalizeId(value._id);
      if (value.testId) return normalizeId(value.testId);
      if (value.id) return normalizeId(value.id);
      if (typeof value.toString === "function") {
        const stringValue = value.toString();
        return stringValue === "[object Object]" ? "" : stringValue;
      }
    }
    return String(value);
  }, []);

  const getTestId = useCallback(
    (test) => normalizeId(test?._id || test?.id || test),
    [normalizeId]
  );
  const testOptions = availableTests.map((test) => {
    const testId = getTestId(test);
    return {
      value: testId,
      label: test.title || test.name || testId,
    };
  }).filter((option) => option.value);
  
  useEffect(() => {
     const fetchPackage = async () => {
    try {
      setLoading(true);
      const res = await AuthInterceptor(`/admin/package/${id}`, {
        method: "GET",
      });

      const data = res.data?.r;

      if (!data) return;

      setTitle(data.title || "");
      setDescription(data.description || "");
      setAgeLimit(data.ageLimit || "");
      setReport(data.report || "");
      setFasting(data.fasting || "");
      setOriginalPrice(data.originalPrice || "");
      setDiscountedPrice(data.discountedPrice || "");
      setDiscountPercent(data.discountPercent || "");
      setGstSlab(String(data.gstSlab ?? data.gst ?? 0));
      setRecommendedGender(data.recommendedGender || "Both");
      setSampleType(data.sampleType || "");
      setTestCriteria(data.testCriteria || "");
      setTestInstruction(data.testInstruction || "");
      const selectedTests = [
        ...(Array.isArray(data.testIds) ? data.testIds : []),
        ...(Array.isArray(data.tests) ? data.tests : []),
      ];
      const selectedIds = Array.from(
        new Set(
          selectedTests
            .map((test) =>
              normalizeId(test?.testId || test?.test?._id || test?.test || test)
            )
            .filter(Boolean)
        )
      );
      setTestIds(selectedIds);
      if (Array.isArray(data.tests) && data.tests.length) {
        setAvailableTests((prev) => {
          const merged = [...prev];
          data.tests.forEach((test) => {
            const testId = normalizeId(test?.testId || test?._id || test);
            if (!testId || merged.some((item) => getTestId(item) === testId)) return;
            merged.push({
              ...test,
              _id: testId,
              title: test.title || test.name || testId,
            });
          });
          return merged;
        });
      }
      setIsActive(data.isActive ?? true);
    } catch (error) {
      setSnack({
        notification: {
          message: "Failed to load package details",
          type: "error",
        },
      });
    } finally {
      setLoading(false);
    }
  };
    fetchPackage();
  }, [id, normalizeId, getTestId]);

  useEffect(() => {
    const fetchPackageOptions = async () => {
      try {
        const testsResponse = await AuthInterceptor("/admin/test?page=1&limit=500", {
          method: "GET",
        });
        setAvailableTests(getRows(testsResponse.data));
      } catch (error) {
        setAvailableTests([]);
      }
    };

    fetchPackageOptions();
  }, []);

  useEffect(() => {
    const mrp = Number(originalPrice);
    const selling = Number(discountedPrice);
    if (mrp > 0 && selling >= 0 && selling <= mrp) {
      setDiscountPercent(String(Math.round(((mrp - selling) / mrp) * 100)));
    } else {
      setDiscountPercent("");
    }
  }, [originalPrice, discountedPrice]);

  const validateForm = () => {
    let newErrors = {};

    if (!title.trim()) newErrors.title = "Title is required";
    if (!description.trim()) newErrors.description = "Description is required";
    if (!ageLimit.trim()) newErrors.ageLimit = "Age limit is required";
    if (!report.trim()) newErrors.report = "Report is required";
    if (!fasting.trim()) newErrors.fasting = "Fasting is required";
    if (!sampleType.trim()) newErrors.sampleType = "Sample type is required";
    if (!testCriteria.trim()) newErrors.testCriteria = "Test criteria is required";
    if (!testInstruction.trim()) newErrors.testInstruction = "Test instruction is required";

    if (!originalPrice || Number(originalPrice) <= 0)
      newErrors.originalPrice = "Valid original price required";

    if (discountPercent && (discountPercent < 0 || discountPercent > 100))
      newErrors.discountPercent = "Discount % must be 0–100";

    

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

 
  const handleUpdate = async (e) => {
    
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);

      const body = {
        title,
        description,
        ageLimit,
        report,
        fasting,
        originalPrice,
        discountedPrice,
        discountPercent,
        gstSlab,
        recommendedGender,
        sampleType,
        testCriteria,
        testInstruction,
        isActive,
        testIds: testIds.map((t) => t),
      };

      const res = await AuthInterceptor(`/admin/package/${id}`, {
        method: "PUT",
        body: JSON.stringify(body),
      });
      if (res.data?.s === 200) {
        setSnack({
          notification: {
            message: "Package updated successfully",
            type: "success",
          },
        });

        setTimeout(() => navigate(-1), 1200);
      } else {
        throw new Error("Update failed");
      }
    } catch (error) {
      setSnack({
        notification: {
          message: error.message || "Error updating package",
          type: "error",
        },
      });
    } finally {
      setLoading(false);
    }
  };

  
  return (
    <>
      {loading && <Spinnerr show />}
      {snack && (
        <Toaster
          notification={snack.notification}
          onClose={() => setSnack(null)}
        />
      )}

      <div className="card shadow-sm border-0 rounded-3 p-3">
        <div className="d-flex justify-content-between mb-3">
          <h2>Edit Package</h2>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => navigate(-1)}
          >
            ← Back
          </button>
        </div>

        <form onSubmit={handleUpdate}>
            <div className="row">
              {/* Title */}
              <div className="col-md-6 mb-3">
                <label className="form-label">Title</label>
                <input
                  className="form-control"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                />
                {errors.title && (
                  <div className="text-danger mt-1">{errors.title}</div>
                )}
              </div>

              {/* Age Limit */}
              <div className="col-md-6 mb-3">
                <label className="form-label">Age Limit</label>
                <input
                  className="form-control"
                  value={ageLimit}
                  onChange={(e) => setAgeLimit(e.target.value)}
                />
                {errors.ageLimit && (
                  <div className="text-danger mt-1">{errors.ageLimit}</div>
                )}
              </div>

              {/* Description */}
              <div className="col-12 mb-3">
                <label className="form-label">Description</label>
                <textarea
                  className="form-control"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
                {errors.description && (
                  <div className="text-danger mt-1">{errors.description}</div>
                )}
              </div>

              {/* Report */}
              <div className="col-md-6 mb-3">
                <label className="form-label">Report</label>
                <input
                  className="form-control"
                  value={report}
                  onChange={(e) => setReport(e.target.value)}
                />
                {errors.report && (
                  <div className="text-danger mt-1">{errors.report}</div>
                )}
              </div>

              {/* Sample Type */}
              <div className="col-md-6 mb-3">
                <label className="form-label">Sample Type</label>
                <input
                  className="form-control"
                  value={sampleType}
                  onChange={(e) => setSampleType(e.target.value)}
                />
                {errors.sampleType && (
                  <div className="text-danger mt-1">{errors.sampleType}</div>
                )}
              </div>

              {/* Pricing */}
              <div className="col-md-6 mb-3">
                <label className="form-label">MRP (₹)</label>
                <input
                  type="number"
                  className="form-control"
                  value={originalPrice}
                  onChange={(e) => setOriginalPrice(e.target.value)}
                />
                {errors.originalPrice && (
                  <div className="text-danger mt-1">
                    {errors.originalPrice}
                  </div>
                )}
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Discounted Price (₹)</label>
                <input
                  type="number"
                  className="form-control"
                  value={discountedPrice}
                  onChange={(e) => setDiscountedPrice(e.target.value)}
                />
                {errors.discountedPrice && (
                  <div className="text-danger mt-1">
                    {errors.discountedPrice}
                  </div>
                )}
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Discount Percent (%)</label>
                <input
                  type="number"
                  className="form-control"
                  value={discountPercent}
                  readOnly
                />
                {errors.discountPercent && (
                  <div className="text-danger mt-1">
                    {errors.discountPercent}
                  </div>
                )}
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">GST Slab</label>
                <select
                  className="form-select"
                  value={gstSlab}
                  onChange={(e) => setGstSlab(e.target.value)}
                >
                  <option value="0">GST 0%</option>
                  <option value="5">GST 5%</option>
                  <option value="18">GST 18%</option>
                </select>
              </div>

              {/* Gender */}
              <div className="col-md-6 mb-3">
                <label className="form-label">Recommended Gender</label>
                <select
                  className="form-select"
                  value={recommendedGender}
                  onChange={(e) => setRecommendedGender(e.target.value)}
                >
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Both">Both</option>
                </select>
                {errors.recommendedGender && (
                  <div className="text-danger mt-1">
                    {errors.recommendedGender}
                  </div>
                )}
              </div>

              {/* Fasting */}
              <div className="col-md-6 mb-3">
                <label className="form-label">Fasting</label>
                <input
                  className="form-control"
                  placeholder="e.g. Yes, 8 hrs"
                  value={fasting}
                  onChange={(e) => setFasting(e.target.value)}
                />
                {errors.fasting && (
                  <div className="text-danger mt-1">{errors.fasting}</div>
                )}
              </div>

              {/* Instructions */}
              <div className="col-12 mb-3">
                <label className="form-label">Test Instruction</label>
                <textarea
                  className="form-control"
                  value={testInstruction}
                  onChange={(e) => setTestInstruction(e.target.value)}
                />
                {errors.testInstruction && (
                  <div className="text-danger mt-1">
                    {errors.testInstruction}
                  </div>
                )}
              </div>

              {/* Note */}
              <div className="col-12 mb-3">
                <label className="form-label">Note</label>
                <textarea
                  className="form-control"
                  value={testCriteria}
                  onChange={(e) => setTestCriteria(e.target.value)}
                />
                {errors.testCriteria && (
                  <div className="text-danger mt-1">{errors.testCriteria}</div>
                )}
              </div>

              {/* Tests */}
              <div className="col-12 mb-3">
                <label className="form-label">Tests</label>
                <SearchableMultiSelectModal
                  title="Select Tests"
                  options={testOptions}
                  selectedValues={testIds}
                  onChange={setTestIds}
                  buttonLabel="Select tests"
                  emptyLabel="No tests selected"
                  disabled={loading}
                />
              </div>

              {/* Active */}
              <div className="col-md-6 mb-3 form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  id="activeCheck"
                />
                <label className="form-check-label" htmlFor="activeCheck">
                  Active
                </label>
              </div>
            </div>

            <div className="text-end">
              <button
                className="btn btn-outline-custom"
                type="submit"
                disabled={loading}
              >
                {loading ? "Saving..." : "Save Package"}
              </button>
            </div>
          </form>
      </div>
    </>
  );
};

export default EditPackageModel;
